import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { buildVaultSetup, decryptJson, decryptShared, encryptForRecipient, encryptJson, newRecoveryWrap, rewrapWithPassword, unlockVault } from "@/lib/vaultCrypto";
import { checkPasswords } from "@/lib/breachCheck";
import { toast } from "sonner";

const BREACH_IDLE = { status: "idle", results: {}, leaked: 0, checked: 0, checkedAt: null, error: "", progress: null, runId: 0 };

const VaultContext = createContext(null);

/**
 * Holder hvelvnøkkelen KUN i minnet. Låses ved inaktivitet, utlogging, eller manuelt.
 * status: loading | none | locked | unlocked | error
 */
export const VaultProvider = ({ children }) => {
  const { user, isOwner, activeWorkspace } = useAuth();
  // Passord-modulen kan være slått av for den aktive virksomheten (hvelvet er felles for kontoen, men skjules da)
  const passordOff = !!activeWorkspace && activeWorkspace.modules?.passord === false;
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [entries, setEntries] = useState([]);
  const [shared, setShared] = useState([]);
  const [busy, setBusy] = useState(false);
  const keysRef = useRef(null); // { vk, vkBytes, privateKey, authToken, publicKeyB64 }
  const lockTimer = useRef(null);
  const [lockedAt, setLockedAt] = useState(null);
  // Hvordan hvelvet sist ble låst opp: "password" | "recovery" | null. Etter gjenopprettingskode skal brukeren
  // velge nytt hovedpassord (og få ny kode) – `recoveryFollowUp` styrer den veiledningen.
  const [unlockedVia, setUnlockedVia] = useState(null);
  const [recoveryFollowUp, setRecoveryFollowUp] = useState(false);
  // Lekkasjesjekk: resultater KUN i minnet (entry_id -> antall treff). Nullstilles ved låsing.
  const [breach, setBreach] = useState(BREACH_IDLE);
  const breachAbort = useRef(null);
  const breachFingerprint = useRef("");

  const settings = useMemo(() => ({ quick_open_mode: "open_only", auto_lock_minutes: 5, clipboard_clear_seconds: 30, breach_check_enabled: true, ...(meta?.settings || {}) }), [meta]);

  const vaultHeaders = useCallback(() => (keysRef.current ? { "X-Vault-Auth": keysRef.current.authToken } : {}), []);

  const refreshMeta = useCallback(async () => {
    if (!user || !isOwner || passordOff) { setStatus("none"); return null; }
    try {
      const r = await api.get("/vault");
      setMeta(r.data);
      setStatus((s) => (r.data.exists ? (keysRef.current ? "unlocked" : "locked") : "none"));
      setError("");
      return r.data;
    } catch (e) {
      setError(errorMessage(e));
      setStatus("error");
      return null;
    }
  }, [user, isOwner, passordOff]);

  const lock = useCallback((reason) => {
    const wasUnlocked = !!keysRef.current;
    keysRef.current = null;
    setEntries([]);
    setShared([]);
    breachAbort.current?.abort();
    breachFingerprint.current = "";
    setBreach(BREACH_IDLE);
    if (lockTimer.current) clearTimeout(lockTimer.current);
    setUnlockedVia(null);
    setRecoveryFollowUp(false);
    setStatus((s) => (s === "none" || s === "loading" ? s : "locked"));
    if (wasUnlocked) setLockedAt(Date.now());
    if (reason === "auto") toast("Hvelvet ble låst etter inaktivitet.", { id: "vault-autolock" });
  }, []);

  const bumpActivity = useCallback(() => {
    if (!keysRef.current) return;
    if (lockTimer.current) clearTimeout(lockTimer.current);
    lockTimer.current = setTimeout(() => lock("auto"), Math.max(1, settings.auto_lock_minutes) * 60 * 1000);
  }, [lock, settings.auto_lock_minutes]);

  useEffect(() => {
    refreshMeta();
  }, [refreshMeta]);

  // Lås ved utlogging / kontobytte
  useEffect(() => {
    if (!user || !isOwner) lock();
  }, [user?.id, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aktivitetslytting for auto-lås
  useEffect(() => {
    const h = () => bumpActivity();
    window.addEventListener("click", h);
    window.addEventListener("keydown", h);
    return () => {
      window.removeEventListener("click", h);
      window.removeEventListener("keydown", h);
    };
  }, [bumpActivity]);

  const decryptAll = useCallback(async (items) => {
    const vk = keysRef.current?.vk;
    const out = [];
    for (const it of items) {
      try {
        const data = await decryptJson(vk, it.blob);
        out.push({ id: it.id, created_at: it.created_at, updated_at: it.updated_at, shares: it.shares || [], ...data });
      } catch {
        out.push({ id: it.id, created_at: it.created_at, updated_at: it.updated_at, shares: it.shares || [], title: "(Kunne ikke dekryptere)", corrupt: true });
      }
    }
    return out;
  }, []);

  const loadEntries = useCallback(async () => {
    if (!keysRef.current) return;
    const r = await api.get("/vault/entries", { headers: vaultHeaders() });
    setEntries(await decryptAll(r.data.items));
  }, [decryptAll, vaultHeaders]);

  const loadShared = useCallback(async () => {
    if (!keysRef.current) return;
    const r = await api.get("/vault/shared-with-me", { headers: vaultHeaders() });
    const out = [];
    for (const s of r.data.items) {
      try {
        const data = await decryptShared(keysRef.current.privateKey, s);
        out.push({ id: s.id, entry_id: s.entry_id, owner: s.owner, updated_at: s.updated_at, ...data, readonly: true });
      } catch {
        out.push({ id: s.id, entry_id: s.entry_id, owner: s.owner, updated_at: s.updated_at, title: "(Kunne ikke dekryptere)", corrupt: true, readonly: true });
      }
    }
    setShared(out);
  }, [vaultHeaders]);

  const afterUnlock = useCallback(async (keys, via = "password") => {
    keysRef.current = keys;
    setUnlockedVia(via);
    setRecoveryFollowUp(via === "recovery");
    setStatus("unlocked");
    bumpActivity();
    await Promise.all([loadEntries(), loadShared()]);
  }, [bumpActivity, loadEntries, loadShared]);

  const setup = useCallback(async (masterPassword) => {
    setBusy(true);
    try {
      const { payload, keys, recoveryCode } = await buildVaultSetup(masterPassword);
      const r = await api.post("/vault/setup", payload);
      setMeta(r.data);
      await afterUnlock(keys);
      return recoveryCode;
    } finally {
      setBusy(false);
    }
  }, [afterUnlock]);

  const unlock = useCallback(async (secret, useRecovery = false) => {
    setBusy(true);
    try {
      const m = meta?.exists ? meta : await refreshMeta();
      if (!m?.exists) throw new Error("Hvelvet er ikke satt opp");
      const keys = await unlockVault(m, secret, useRecovery);
      await afterUnlock(keys, useRecovery ? "recovery" : "password");
      return true;
    } catch (e) {
      if (e?.name === "OperationError" || /decrypt|tag|operation/i.test(String(e?.message || e?.name))) return false;
      throw e;
    } finally {
      setBusy(false);
    }
  }, [meta, refreshMeta, afterUnlock]);

  /**
   * Kjører lekkasjesjekk (HIBP k-anonymitet) på dekrypterte oppføringer. Kun 5 hex-tegn av SHA-1 sendes ut;
   * passord/resultat forlater aldri nettleseren. `force` kjører selv om samme sett allerede er sjekket.
   */
  const runBreachCheck = useCallback(async (force = false) => {
    if (!keysRef.current) return null;
    const list = entries.filter((e) => !e.corrupt && e.password);
    const fp = list.map((e) => `${e.id}:${e.updated_at || ""}`).sort().join("|");
    if (!force && fp === breachFingerprint.current) return null;
    breachAbort.current?.abort();
    const ctrl = new AbortController();
    breachAbort.current = ctrl;
    const runId = Date.now();
    breachFingerprint.current = fp;
    if (!list.length) {
      setBreach({ ...BREACH_IDLE, status: "done", checkedAt: new Date().toISOString(), runId });
      return { leaked: 0, checked: 0 };
    }
    setBreach((b) => ({ ...b, status: "running", error: "", progress: { done: 0, total: 0 }, runId }));
    try {
      const res = await checkPasswords(list.map((e) => e.password), {
        signal: ctrl.signal,
        onProgress: (done, total) => setBreach((b) => (b.runId === runId ? { ...b, progress: { done, total } } : b)),
      });
      if (ctrl.signal.aborted) return null;
      const results = {};
      let leaked = 0;
      for (const e of list) {
        const n = res.get(e.password) || 0;
        if (n > 0) { results[e.id] = n; leaked++; }
      }
      const next = { status: "done", results, leaked, checked: list.length, checkedAt: new Date().toISOString(), error: "", progress: null, runId };
      setBreach(next);
      return next;
    } catch (e) {
      if (ctrl.signal.aborted || e?.name === "AbortError") return null;
      breachFingerprint.current = ""; // tillat nytt forsøk
      setBreach((b) => ({ ...b, status: "error", error: e?.message || "Lekkasjesjekk mislyktes", progress: null, runId }));
      return null;
    }
  }, [entries]);

  // Automatisk sjekk når hvelvet låses opp / oppføringer endres (kan slås av i hvelvinnstillinger)
  useEffect(() => {
    if (status !== "unlocked" || !keysRef.current) return;
    if (!settings.breach_check_enabled) {
      breachAbort.current?.abort();
      breachFingerprint.current = "";
      setBreach((b) => (b.status === "disabled" ? b : { ...BREACH_IDLE, status: "disabled" }));
      return;
    }
    const t = setTimeout(() => { runBreachCheck(false); }, 250);
    return () => clearTimeout(t);
  }, [status, entries, settings.breach_check_enabled, runBreachCheck]);

  const buildShares = useCallback(async (data, recipients) => {
    const out = [];
    for (const rcp of recipients) {
      if (!rcp.public_key) continue;
      const enc = await encryptForRecipient(rcp.public_key, data);
      out.push({ recipient_id: rcp.id, ...enc });
    }
    return out;
  }, []);

  /** data = klartekst-felter. recipients = [{id, public_key}] som allerede har tilgang (re-kryptering ved oppdatering). */
  const saveEntry = useCallback(async (data, existingId = null, recipients = []) => {
    if (!keysRef.current) throw new Error("Hvelvet er låst");
    const clean = { ...data };
    delete clean.id; delete clean.created_at; delete clean.updated_at; delete clean.shares; delete clean.corrupt; delete clean.readonly;
    const blob = await encryptJson(keysRef.current.vk, clean);
    const shares = await buildShares(clean, recipients);
    const r = existingId ? await api.put(`/vault/entries/${existingId}`, { blob, shares }, { headers: vaultHeaders() }) : await api.post("/vault/entries", { blob, shares }, { headers: vaultHeaders() });
    await loadEntries();
    bumpActivity();
    return r.data;
  }, [buildShares, loadEntries, vaultHeaders, bumpActivity]);

  const deleteEntry = useCallback(async (id) => {
    await api.delete(`/vault/entries/${id}`, { headers: vaultHeaders() });
    await loadEntries();
  }, [loadEntries, vaultHeaders]);

  const shareEntry = useCallback(async (entry, recipient) => {
    const clean = { ...entry };
    delete clean.id; delete clean.created_at; delete clean.updated_at; delete clean.shares; delete clean.corrupt; delete clean.readonly;
    const enc = await encryptForRecipient(recipient.public_key, clean);
    await api.post(`/vault/entries/${entry.id}/shares`, { recipient_id: recipient.id, ...enc }, { headers: vaultHeaders() });
    await loadEntries();
  }, [loadEntries, vaultHeaders]);

  const revokeShare = useCallback(async (entryId, recipientId) => {
    await api.delete(`/vault/entries/${entryId}/shares/${recipientId}`, { headers: vaultHeaders() });
    await loadEntries();
  }, [loadEntries, vaultHeaders]);

  const updateSettings = useCallback(async (patch) => {
    const r = await api.put("/vault/settings", patch);
    setMeta((m) => ({ ...m, settings: r.data }));
    return r.data;
  }, []);

  const changeMaster = useCallback(async (currentPassword, newPassword) => {
    const m = meta;
    // Verifiser gjeldende passord lokalt før re-wrap
    await unlockVault(m, currentPassword, false);
    const wrap = await rewrapWithPassword(keysRef.current.vkBytes, newPassword);
    const r = await api.put("/vault/master", wrap, { headers: vaultHeaders() });
    setMeta((prev) => ({ ...prev, ...r.data }));
  }, [meta, vaultHeaders]);

  const regenerateRecovery = useCallback(async () => {
    const { recoveryCode, recovery } = await newRecoveryWrap(keysRef.current.vkBytes);
    const r = await api.put("/vault/recovery", { recovery }, { headers: vaultHeaders() });
    setMeta((prev) => ({ ...prev, ...r.data }));
    return recoveryCode;
  }, [vaultHeaders]);

  const deleteVault = useCallback(async (masterPassword) => {
    await unlockVault(meta, masterPassword, false); // krever hovedpassord
    await api.delete("/vault", { headers: vaultHeaders() });
    keysRef.current = null;
    setEntries([]);
    setShared([]);
    setUnlockedVia(null);
    setRecoveryFollowUp(false);
    setMeta({ exists: false, settings: {} });
    setStatus("none");
  }, [meta, vaultHeaders]);

  /**
   * Etter opplåsing med gjenopprettingskode: sett NYTT hovedpassord (VK pakkes inn på nytt – innholdet krypteres ikke om)
   * og lag en ny gjenopprettingskode (den gamle slutter å virke). Returnerer den nye koden.
   */
  const setMasterAfterRecovery = useCallback(async (newPassword) => {
    if (!keysRef.current) throw new Error("Hvelvet er låst");
    const wrap = await rewrapWithPassword(keysRef.current.vkBytes, newPassword);
    const r1 = await api.put("/vault/master", wrap, { headers: vaultHeaders() });
    const { recoveryCode, recovery } = await newRecoveryWrap(keysRef.current.vkBytes);
    const r2 = await api.put("/vault/recovery", { recovery }, { headers: vaultHeaders() });
    setMeta((prev) => ({ ...prev, ...r1.data, ...r2.data }));
    setUnlockedVia("password");
    bumpActivity();
    return recoveryCode;
  }, [vaultHeaders, bumpActivity]);

  const dismissRecoveryFollowUp = useCallback(() => setRecoveryFollowUp(false), []);

  /**
   * Nullstilling for bruker som er låst ute (verken hovedpassord eller gjenopprettingskode).
   * Krever KONTOPASSORDET (innloggingspassordet), ikke hovedpassordet. Alt kryptert innhold slettes permanent.
   */
  const resetVault = useCallback(async (accountPassword, confirmText = "SLETT") => {
    const r = await api.post("/vault/reset", { account_password: accountPassword, confirm: (confirmText || "").trim().toUpperCase() });
    keysRef.current = null;
    if (lockTimer.current) clearTimeout(lockTimer.current);
    setEntries([]);
    setShared([]);
    setBreach(BREACH_IDLE);
    setUnlockedVia(null);
    setRecoveryFollowUp(false);
    setLockedAt(null);
    setMeta({ exists: false, settings: {} });
    setStatus("none");
    return r.data;
  }, []);

  const value = useMemo(() => ({
    status, meta, error, entries, shared, settings, busy, lockedAt, breach, unlockedVia, recoveryFollowUp,
    refreshMeta, setup, unlock, lock, loadEntries, loadShared, saveEntry, deleteEntry, shareEntry, revokeShare, updateSettings, changeMaster, regenerateRecovery, deleteVault, bumpActivity, runBreachCheck,
    setMasterAfterRecovery, dismissRecoveryFollowUp, resetVault,
    isUnlocked: status === "unlocked",
  }), [status, meta, error, entries, shared, settings, busy, lockedAt, breach, unlockedVia, recoveryFollowUp, refreshMeta, setup, unlock, lock, loadEntries, loadShared, saveEntry, deleteEntry, shareEntry, revokeShare, updateSettings, changeMaster, regenerateRecovery, deleteVault, bumpActivity, runBreachCheck, setMasterAfterRecovery, dismissRecoveryFollowUp, resetVault]);

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
};

export const useVault = () => {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error("useVault må brukes innenfor VaultProvider");
  return ctx;
};
